/**
 * eventContacts — one-shot reads of the gated event `participantContacts`
 * collection (docs/spec/10c-events-participant-contacts.md).
 *
 * The entrant's personal and health data (birth year, allergies, medical
 * notes, guardian name / phone / e-mail, consent, payment note) lives in
 * `gyms/{gymId}/events/{eventId}/participantContacts/{entrantId}`, NOT on the
 * participant / waitlist documents, which every active gym member can read.
 * The entrant id equals the participant / waitlist document id.
 *
 * Why plain functions that take `db` and never a listener: a contact document
 * is not real-time data, and a standing listener would stream the gym's whole
 * set of minors' health data into memory. Reads happen on demand — an editor
 * opens, an export is clicked. The client never writes this collection.
 */
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { PARTICIPANT_CONTACT_FIELDS } from "@sendly/shared";

/**
 * One entrant's contact document, or null when it does not exist (a normal
 * state: nothing was stored). A FAILED read (network, permission) is NOT null —
 * it rejects. The two must stay distinguishable: the edit form submits a
 * `set(merge)` patch, so treating a failure as "nothing stored" would let it
 * blank the entrant's health and guardian data. The caller decides how to
 * surface the failure (the edit modal blocks the save and offers a retry).
 */
export async function fetchParticipantContact(db, gymId, eventId, entrantId) {
  if (!gymId || !eventId || !entrantId) return null;
  const snap = await getDoc(doc(db, "gyms", gymId, "events", eventId, "participantContacts", entrantId));
  return snap.exists() ? snap.data() : null;
}

/**
 * Every contact document of an event as `{ [entrantId]: data }`. A single
 * collection read, allowed by the rules only for staff, the organizer, a
 * check-in coach and `db.export` holders — the circle the callers' buttons
 * are already behind. A refused read yields `{}` (blank cells in the export)
 * rather than failing the whole PDF / Excel — unlike the single-doc read this is
 * safe, because an export writes nothing back. It is logged, so a gap is at
 * least visible in the console (the callers have no error flag to set).
 */
export async function fetchParticipantContacts(db, gymId, eventId) {
  if (!gymId || !eventId) return {};
  try {
    const snap = await getDocs(collection(db, "gyms", gymId, "events", eventId, "participantContacts"));
    const byId = {};
    snap.docs.forEach(d => { byId[d.id] = d.data(); });
    return byId;
  } catch (err) {
    console.warn("participantContacts list read failed — export rows will have blank contact cells", err);
    return {};
  }
}

/**
 * Participant rows with their contact fields merged in by id. A participant
 * without a contact document is returned as is (blank cells downstream).
 * Only the nine contact fields are taken, so bookkeeping keys of the contact
 * document (`lastModifiedBy`, `updatedAt`) never overwrite the row's own.
 */
export function mergeContacts(rows, contacts) {
  if (!Array.isArray(rows)) return [];
  if (!contacts) return rows;
  return rows.map(row => {
    const contact = contacts[row.id];
    if (!contact) return row;
    const merged = { ...row };
    for (const key of PARTICIPANT_CONTACT_FIELDS) {
      if (contact[key] !== undefined) merged[key] = contact[key];
    }
    return merged;
  });
}

/**
 * The participant rows with their contact data merged in, read through the
 * events hook (so a caller that holds the hook needs no `db` / gym id of its
 * own). A hook without the loader — a test double — merges nothing.
 */
export async function withContacts(eventsHook, eventId, rows) {
  const contacts = await eventsHook?.loadParticipantContacts?.(eventId);
  return mergeContacts(rows, contacts);
}
